import { afterNextRender, computed, Injectable, signal } from '@angular/core';

export interface CartItem {
  /** Contentful entry ID */
  productId: string;
  sku: string;
  name: string;
  imageUrl: string | null;
  /** e.g. "1 кг" — null when product has no weight options */
  weightLabel: string | null;
  /** unit part of the weight, e.g. "кг" */
  unit: string | null;
  price: number | null;
  quantity: number;
}

/** Unique key for a cart line (same product can appear with different weights). */
function lineKey(productId: string, weightLabel: string | null): string {
  return `${productId}::${weightLabel ?? ''}`;
}

const STORAGE_KEY = 'milkhouse_cart';

@Injectable({ providedIn: 'root' })
export class CartStore {
  /** Empty during prerender so the HTML matches the first client render. */
  readonly items = signal<CartItem[]>([]);

  constructor() {
    afterNextRender(() => {
      const stored = this.load();
      if (stored.length > 0) this.items.set(stored);
    });
  }

  readonly totalQuantity = computed(() =>
    this.items().reduce((sum, i) => sum + i.quantity, 0),
  );

  readonly totalPrice = computed(() =>
    this.items().reduce((sum, i) => sum + (i.price ?? 0) * i.quantity, 0),
  );

  quantityOf(productId: string, weightLabel: string | null): number {
    const key = lineKey(productId, weightLabel);
    return this.items().find(i => lineKey(i.productId, i.weightLabel) === key)?.quantity ?? 0;
  }

  /** Add `qty` units (or increment existing line). */
  add(item: Omit<CartItem, 'quantity'>, qty = 1): void {
    const amount = Math.max(1, Math.floor(qty));
    const key = lineKey(item.productId, item.weightLabel);
    const existing = this.items().find(
      i => lineKey(i.productId, i.weightLabel) === key,
    );
    if (existing) {
      this.updateQty(item.productId, item.weightLabel, existing.quantity + amount);
    } else {
      this.items.update(list => [...list, { ...item, quantity: amount }]);
      this.save();
    }
  }

  /** Set quantity; removes the line when qty reaches 0. */
  updateQty(productId: string, weightLabel: string | null, qty: number): void {
    const key = lineKey(productId, weightLabel);
    this.items.update(list =>
      qty <= 0
        ? list.filter(i => lineKey(i.productId, i.weightLabel) !== key)
        : list.map(i =>
            lineKey(i.productId, i.weightLabel) === key ? { ...i, quantity: qty } : i,
          ),
    );
    this.save();
  }

  remove(productId: string, weightLabel: string | null): void {
    this.updateQty(productId, weightLabel, 0);
  }

  clear(): void {
    this.items.set([]);
    this.save();
  }

  // ── persistence ────────────────────────────────────────────────────────────

  private load(): CartItem[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items()));
    } catch { /* quota exceeded — silently ignore */ }
  }
}
